<template lang="html">
  <Layout currentMenu="Account" subMenu="Your Listings">
    <div class="section" v-if="is_super_admin">
      <div v-if="loadingVendors">
        <div class="gap-3x" />
        <Spinner />
        <div class="gap-3x" />
      </div>

      <div v-if="!loadingVendors">
        <h4 class="black">
          Selected Vendor: {{ selectedVendorName }}
        </h4>
        <div class="gap-3x" />
        <h4 class="black">
          You can change vendor here to view other vendor's listings
        </h4>
        <div class="gap-3x" />
        <div id="listing-dropdown"></div>
      </div>
    </div>

    <LoadingSection
      title="Your Listings"
      :loading="loading || loadingVendors"
      :autoPadding="false"
    >
      <template v-slot:content>
        <div v-if="isSuspendedVendor" class="suspended">
          <i class="si-icon-bold si-icon-6x si-icon-text-5x si-warning_circle"></i>
          <div class="body-17">
            Account suspended
          </div>
        </div>
        <div class="section-field flex-box wrap">
          <h3 class="black pull-left bgap-24">Products</h3>
          <div class="product-list pull-right flex-box wrap body-17 black">
            <div class="sub-section">
              <div class="bgap-12">Currently listed</div>
              <h3>{{ listingCount }}</h3>
            </div>
            <div class="sub-section">
              <div class="bgap-12">Listings still available</div>
              <h3>{{ availableListings }}</h3>
              <a
                v-if="listingCount + availableListings == 1"
                href="https://accounts.symbium.com/shop"
                target="_blank"
                ><div class="gap-12">Add more listings</div></a
              >
            </div>
          </div>
        </div>
      </template>
    </LoadingSection>

    <LoadingSection
      title="Products"
      :loading="loading || loadingVendors"
      :autoPadding="false"
    >
      <template v-slot:content>
        <div class="section-field product-section">
          <div
            :class="['body-15-medium', 'black', 'flex-box', 'add-new-product', isSuspendedVendor ? 'disabled' : '']"
            @click="() => !isSuspendedVendor && openProductEdit()"
          >
            <i class="si-icon si-plus si-icon-8x si-icon-text-5x"></i>
            Add New Product
          </div>
          <div class="gap-4x" />
          <ListingRow
            :onClick="() => openProductEdit(listing)"
            v-for="listing in listings"
            :listing="listing"
            :key="listing.designId"
          />
          <div v-if="paginateData.page_count > 1">
            <div class="gap-3x" />
            <PaginationContainer
              :paginateData="paginateData"
              @page-change="reloadDesigns"
              klass="section-field"
            />
            <div class="gap-3x" />
          </div>
        </div>
      </template>
    </LoadingSection>
    
    <ConfirmModal />
  </Layout>
</template>

<script>
import _ from 'lodash'

import Spinner from 'src/components/common/Spinner.vue'
import ConfirmModal from 'src/components/common/modals/ConfirmModal.vue'
import PaginationContainer from 'src/components/common/utility/PaginationContainer.vue'
import EventBus from 'src/components/EventBus.js'
import ListingRow from './ListingRow.vue'
import LoadingSection from 'src/components/dashboard/shares/LoadingSection.vue'
import Layout from 'src/components/dashboard/shares/Layout.vue'

export default {
  components: {
    Spinner, ListingRow,
    PaginationContainer, ConfirmModal, LoadingSection, Layout
  },
  methods: {
    checkAdmin(data) {
      this.is_super_admin = data.is_super_admin
      if(this.is_super_admin) {
        this.loadingVendors = true;
        this.$vendor.fetchVendorIds(this.assignVendors)
      } else {
        this.loadingVendors = false;
        this.isSuspendedVendor = data.is_suspended_vendor
      }
    },
    assignVendors(response) {
      this.loadingVendors = false
      this.vendors = response.data.vendors
    },
    vendorChanged(vendorId) {
      this.vendorId = vendorId
      this.reloadDesigns(1)
    },
    assignData(response) {
      this.paginateData = _.pick(response, [
        "page_count",
        "per_page",
        "last_page",
        "first_page",
        "total_count",
        "page",
      ])
      this.listings = response.data
      this.listingCount = response.listingCount
      this.availableListings = response.availableListings
      this.vendorId = this.vendorId || response.vendor_id
      this.loading = false
    },
    openProductEdit(listing) {
      if(listing) {
        if(listing.legacy) {
          this.$modal.show('confirm-modal', {
            confirmMessage: `This is a legacy listing and cannot be edited in the
            Vendor Dashboard directly. You may, however, create a new listing to
            replace this legacy listing. To initiate this process, click the
            <strong>Migrate Listing</strong> button below. Note that the replacement design will
            port over the ADU footprint from your legacy
            listing and any analytics from the legacy design will be preserved.`,
            alert: false,
            actionText: 'Migrate Listing',
            confirmCallBack: () => this.migrateLegacy(listing),
            cancelText: 'Close',
            bold: false
          })
        } else {
          this.$router.push(`/dashboard/product/edit?id=${listing.id}&vendorId=${this.vendorId}`)
        }
      } else {
        this.$router.push(`/dashboard/product/edit?vendorId=${this.vendorId}`)
      }
    },
    reloadDesigns(page) {
      this.loading = true
      this.$design.fetchDesigns({ ...this.designOptions, page });
    },
    migrateLegacy(listing) {
      this.loading = true
      this.$design.migrateLegacy(listing, this.afterMigrate, this.errorCallback)
      this.$modal.hide('confirm-modal')
    },
    afterMigrate(response) {
      this.loading = false
      this.$router.push(`/dashboard/product/edit?id=${response.data.id}&vendorId=${this.vendorId}`)
    },
    errorCallback() {
      this.loading = false
      alert('Something went wrong')
    }
  },
  computed: {
    designOptions() {
      return { ...this.paginateData, vendorId: this.vendorId }
    },
    selectedVendor() {
      return _.find(this.vendors, (vendor) => { return vendor.ID == this.vendorId })
    },
    selectedVendorName() {
      return this.selectedVendor ? this.selectedVendor.name : ''
    }
  },
  data() {
    return {
      loading: true,
      loadingVendors: false,
      paginateData: { page: 1 },
      isSuspendedVendor: false,
      listingCount: 0,
      availableListings: 0,
      vendorId: this.$route.query.vendorId,
      vendors: [],
      is_super_admin: false,
      listings: [],
    }
  },
  created() {
    EventBus.$on('designs-fetched', this.assignData)
    EventBus.$on('user-fetched', this.checkAdmin)
  },
  beforeDestroy() {
    EventBus.$off('designs-fetched', this.assignData)
    EventBus.$off('user-fetched', this.checkAdmin)
  },
  mounted() {
    this.loading = true
    this.$user.fetchMe();
    this.$design.fetchDesigns(this.designOptions);
  },
  watch: {
    loadingVendors() {
      if(!this.loadingVendors){
        window.listingsVueComponent = this
        // Create dropdown
        this.$nextTick(() => {
          const options = this.vendors.map(vendor => ({id: vendor.ID, option: vendor.name}))
          const selectedIndex = options.findIndex(option => option.id === this.vendorId) + 1
          const customDropdownScript = document.createElement('script')

          customDropdownScript.text = `
            new MultiDropdown({
              id: "listing-dropdown",
              options: ${JSON.stringify(options)},
              selectOption: (value) => window.listingsVueComponent.vendorChanged(value.id),
              isMulti: false,
              defaultValueIndex: ${selectedIndex}
            });`
          document.head.appendChild(customDropdownScript)
        })
      }
    }
  }
}
</script>

<style lang="scss">
#listing-dropdown {
  max-width: 332px;
}

.suspended {
  display: flex;
  position: relative;
  top: -24px;
}

.product-list {
  margin-top: -24px;

  .sub-section {
    min-width: 314px;
    margin-top: 24px;

    a,
    a:hover {
      color: #979797;
    }
  }
}

.product-section {
  padding-bottom: 16px !important;
}

.add-new-product {
  align-items: center;
  cursor: pointer;
  
  i {
    font-weight: 600 !important;
  }

  &.disabled {
    cursor: not-allowed;
    color: #979797;
  }
}
</style>
